import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  ArrowRight,
  QrCode,
  BookOpen,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  FileText,
  Sliders,
  BellRing,
  Moon,
  Zap,
  MousePointer,
  CheckCircle2,
} from "lucide-react";

export const NixlevCleanEditorial: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Premium Architectural Editorial Palette (NO AI SLOP GRADIENTS)
  const bgWarm = "#F8F6F0";
  const bgCard = "#FFFFFF";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#059669";
  const greenBright = "#10B981";
  const goldAccent = "#D4A346";
  const textDark = "#111827";
  const textMuted = "#4B5563";
  const borderLight = "#E5E7EB";

  // Subtitles Timed with Voiceover
  const SUBTITLES = [
    { from: 0, to: 130, text: "Pernah gak, lagi tidur jam 2 pagi, tiba-tiba saldo masuk ke rekening?", highlight: "saldo masuk" },
    { from: 130, to: 235, text: "Bukan sihir, ini mesin otomatis Nixlev.", highlight: "mesin otomatis Nixlev" },
    { from: 235, to: 340, text: "Tinggal pajang karyamu dalam satu link bio yang bersih dan elegan.", highlight: "satu link bio" },
    { from: 340, to: 470, text: "Pembeli scan QRIS, file langsung meluncur ke WhatsApp dan Email detik itu juga.", highlight: "ke WhatsApp dan Email" },
    { from: 470, to: 600, text: "Buka tokomu gratis hari ini di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Springs
  const appleSpring = (delay: number) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping: 15.35, stiffness: 115.0, mass: 1.0 },
    });

  const snappySpring = (delay: number) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping: 14, stiffness: 180, mass: 0.8 },
    });

  // Micro-motion breathing
  const ambientFloatY = Math.sin(frame / 22) * 6;

  // Animated Saldo Counter (0 -> 150000)
  const counterProgress = spring({
    frame: frame - 45,
    fps,
    config: { damping: 18, stiffness: 85 },
  });
  const currentSaldo = Math.floor(interpolate(counterProgress, [0, 1], [0, 150000]));
  const formattedSaldo = new Intl.NumberFormat("id-ID").format(currentSaldo);

  // Simulated Mouse Cursor Motion (Gliding in to click at frame 290)
  const cursorX = interpolate(
    frame,
    [240, 285, 305],
    [540, 380, 420],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const cursorY = interpolate(
    frame,
    [240, 285, 305],
    [850, 720, 740],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const isCursorClick = frame >= 285 && frame <= 295;

  const isOutro = frame >= 470;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: isOutro ? greenForest : bgWarm,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: isOutro ? "#FFFFFF" : textDark,
        overflow: "hidden",
        transition: "background-color 0.4s ease",
      }}
    >
      {/* 1. SOLID ARCHITECTURAL DOT MATRIX (ZERO AI SLOP GRADIENTS) */}
      {!isOutro && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(#E2DFD6 1.5px, transparent 1.5px)`,
            backgroundSize: "28px 28px",
            opacity: 0.75,
            pointerEvents: "none",
          }}
        />
      )}

      {/* 2. AUDIO ARCHITECTURE */}
      {/* Voiceover Track */}
      <Audio src={staticFile("nixlev_clean_sample.mp3")} volume={1.0} />

      {/* Subtle Low-Volume Background Music (Ducked) */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* Frame-Accurate Micro-SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 35 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 48 && <Audio src={staticFile("tick.mp3")} volume={0.5} />}
      {frame === 60 && <Audio src={staticFile("tick.mp3")} volume={0.5} />}
      {frame === 75 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 130 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 235 && <Audio src={staticFile("whoosh.mp3")} volume={0.45} />}
      {frame === 285 && <Audio src={staticFile("click.mp3")} volume={0.7} />}
      {frame === 340 && <Audio src={staticFile("ui-success.mp3")} volume={0.75} />}
      {frame === 470 && <Audio src={staticFile("chime.mp3")} volume={0.9} />}

      {/* 3. TRUE HORIZONTAL & GROUNDED VERTICAL CONTAINER */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: 880,
          top: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* BEAT 1: HOOK — THE 02:14 AM NOTIFICATION & LIVE COUNTER (0 - 130 frames / 0 - 4.3s) */}
        {frame < 130 && (
          <div
            style={{
              position: "absolute",
              top: 310,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Top Pill Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: "#EFF6FF",
                border: "1.5px solid #BFDBFE",
                color: "#1D4ED8",
                padding: "10px 26px",
                borderRadius: 30,
                fontSize: 21,
                fontWeight: 800,
                marginBottom: 24,
                boxShadow: "0 4px 15px rgba(37, 99, 235, 0.06)",
                transform: `scale(${appleSpring(0)})`,
              }}
            >
              <Moon size={22} color="#2563EB" />
              02:14 AM — Saat Kamu Tidur Nyenyak
            </div>

            <h1
              style={{
                fontSize: 58,
                fontWeight: 900,
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                marginBottom: 50,
              }}
            >
              Lagi Tidur Jam 2 Pagi, <br />
              <span style={{ color: greenEmerald }}>Tiba-Tiba Saldo Masuk?</span>
            </h1>

            {/* High-End Clean White Bank Card */}
            <div
              style={{
                width: 780,
                backgroundColor: bgCard,
                border: "2px solid #10B981",
                borderRadius: 28,
                padding: "28px 34px",
                display: "flex",
                alignItems: "center",
                gap: 24,
                boxShadow: "0 30px 70px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(27, 59, 43, 0.04)",
                transform: `translateY(${ambientFloatY}px) scale(${interpolate(
                  snappySpring(30),
                  [0, 1],
                  [0.85, 1]
                )})`,
                opacity: interpolate(snappySpring(30), [0, 1], [0, 1]),
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 22,
                  backgroundColor: "#ECFDF5",
                  border: "2px solid #A7F3D0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BellRing size={40} color={greenEmerald} />
              </div>
              <div style={{ flex: 1, textAlign: "left" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: "#065F46" }}>QRIS Pembayaran Berhasil</span>
                  <span style={{ fontSize: 16, color: "#6B7280", fontWeight: 600 }}>Baru saja</span>
                </div>
                {/* LIVE TICKING SALDO COUNTER */}
                <div
                  style={{
                    fontSize: 40,
                    fontWeight: 900,
                    color: "#047857",
                    marginTop: 4,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  +Rp {formattedSaldo}
                </div>
                <div style={{ fontSize: 16, color: textMuted, marginTop: 4 }}>
                  Penjualan otomatis via <b>nixlev.com/@kreator</b>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BEAT 2: THE REVEAL & 3D BROWSER WITH INTERACTION (130 - 340 frames / 4.3 - 11.3s) */}
        {frame >= 130 && frame < 340 && (
          <div
            style={{
              position: "absolute",
              top: 310,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: "#DCFCE7",
                border: "1.5px solid #86EFAC",
                color: "#15803D",
                padding: "10px 26px",
                borderRadius: 30,
                fontSize: 21,
                fontWeight: 800,
                marginBottom: 20,
              }}
            >
              <Zap size={22} />
              Mesin Otomatis Nixlev
            </div>

            <h2
              style={{
                fontSize: 52,
                fontWeight: 900,
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                marginBottom: 35,
              }}
            >
              Satu Link Bio Rapi. <br />
              <span style={{ color: greenEmerald }}>Semua Karyamu Siap Cuan.</span>
            </h2>

            {/* Clean Safari Browser Window */}
            <div
              style={{
                width: 840,
                height: 480,
                backgroundColor: "#FFFFFF",
                borderRadius: 24,
                border: "1.5px solid #D1D5DB",
                boxShadow: "0 35px 80px rgba(0, 0, 0, 0.12), 0 10px 25px rgba(27, 59, 43, 0.06)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                transform: `perspective(1200px) rotateX(${interpolate(
                  frame,
                  [130, 340],
                  [8, 3]
                )}deg) rotateY(${interpolate(
                  frame,
                  [130, 340],
                  [-5, -1]
                )}deg) translateY(${ambientFloatY}px)`,
              }}
            >
              <div
                style={{
                  height: 46,
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #E5E7EB",
                  padding: "0 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                  <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                  <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
                </div>
                <div
                  style={{
                    flex: 1,
                    height: 30,
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#374151",
                    gap: 8,
                  }}
                >
                  <Globe size={15} color={greenBright} />
                  nixlev.com/@kreator
                </div>
              </div>
              <Img
                src={staticFile("nixlev_desktop.png")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top center",
                }}
              />

              {/* Simulated Mouse Cursor Hovering & Clicking */}
              {frame >= 240 && frame < 330 && (
                <div
                  style={{
                    position: "absolute",
                    left: cursorX,
                    top: cursorY,
                    zIndex: 50,
                    pointerEvents: "none",
                    transform: `scale(${isCursorClick ? 0.85 : 1})`,
                    transition: "transform 0.1s ease",
                  }}
                >
                  <MousePointer size={36} color="#111827" fill="#FFFFFF" style={{ filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.3))" }} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* BEAT 3: QRIS & DUAL DELIVERY TO WA & EMAIL (340 - 470 frames / 11.3 - 15.7s) */}
        {frame >= 340 && frame < 470 && (
          <div
            style={{
              position: "absolute",
              top: 310,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: "#FEF3C7",
                border: "1.5px solid #FDE68A",
                color: "#B45309",
                padding: "10px 26px",
                borderRadius: 30,
                fontSize: 21,
                fontWeight: 800,
                marginBottom: 20,
              }}
            >
              Instant QRIS ➔ Auto-Dispatch
            </div>

            <h2 style={{ fontSize: 50, fontWeight: 900, marginBottom: 35 }}>
              Scan QRIS Sekali. <br />
              <span style={{ color: greenEmerald }}>File Meluncur ke WA & Email.</span>
            </h2>

            {/* Central Clean QRIS Card */}
            <div
              style={{
                width: 320,
                backgroundColor: "#111827",
                borderRadius: 26,
                padding: "20px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
                marginBottom: 25,
                transform: `translateY(${ambientFloatY * 0.5}px)`,
              }}
            >
              <div
                style={{
                  width: 170,
                  height: 170,
                  backgroundColor: "#FFFFFF",
                  borderRadius: 16,
                  padding: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QrCode size={150} color="#111827" />
              </div>
              <div style={{ marginTop: 12, fontSize: 18, fontWeight: 800, color: "#34D399" }}>
                ✔ Semua M-Banking & E-Wallet
              </div>
            </div>

            {/* Dual Delivery Stack */}
            <div style={{ width: "100%", display: "flex", gap: 16 }}>
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  border: "2px solid #22C55E",
                  borderRadius: 20,
                  padding: "18px 22px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  boxShadow: "0 10px 30px rgba(34, 197, 94, 0.10)",
                }}
              >
                <MessageSquare size={34} color="#15803D" />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#14532D" }}>Kirim ke WhatsApp</div>
                  <div style={{ fontSize: 14, color: textMuted }}>Link download instan di chat.</div>
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  border: "2px solid #3B82F6",
                  borderRadius: 20,
                  padding: "18px 22px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  boxShadow: "0 10px 30px rgba(59, 130, 246, 0.10)",
                }}
              >
                <Mail size={34} color="#2563EB" />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#1E3A8A" }}>Kirim ke Email</div>
                  <div style={{ fontSize: 14, color: textMuted }}>Invoice resmi & akses file aman.</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BEAT 4: BRAND HERO OUTRO (470 - 600 frames / 15.7 - 20s) */}
        {frame >= 470 && (
          <div
            style={{
              position: "absolute",
              top: 310,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Logo with Soft Ring */}
            <div
              style={{
                padding: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                border: `3px solid ${goldAccent}`,
                boxShadow: "0 0 50px rgba(16, 185, 129, 0.45)",
                transform: `scale(${appleSpring(470)}) translateY(${ambientFloatY * 0.6}px)`,
                marginBottom: 25,
              }}
            >
              <Img src={staticFile("nixlev_logo.png")} style={{ width: 160, height: 160, objectFit: "contain" }} />
            </div>

            <h1 style={{ fontSize: 60, fontWeight: 900, lineHeight: 1.15, marginBottom: 20 }}>
              Toko Jalan 24 Jam. <br />
              <span style={{ color: "#34D399" }}>Cuan Masuk Otomatis.</span>
            </h1>

            <p style={{ fontSize: 24, color: "#CBD5E1", maxWidth: 740, lineHeight: 1.4, marginBottom: 35 }}>
              Nol biaya hosting. Nol drama balas chat. Bikin tokomu gratis dalam 5 menit.
            </p>

            <div
              style={{
                backgroundColor: greenBright,
                color: "#FFFFFF",
                padding: "22px 54px",
                borderRadius: 32,
                fontSize: 28,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
              }}
            >
              <span>Buka Tokomu Gratis di nixlev.com ➔</span>
            </div>
          </div>
        )}

        {/* STRICT LOWER-GROUNDED SAFE ZONE SUBTITLES (Y = 1270 - 1440 px) */}
        {currentSub && (
          <div
            style={{
              position: "absolute",
              top: 1270, // Sesuai pilihan BOZ
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              zIndex: 100,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(18, 24, 20, 0.94)",
                backdropFilter: "blur(14px)",
                padding: "16px 36px",
                borderRadius: 24,
                border: "1px solid rgba(255, 255, 255, 0.16)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
                maxWidth: 840,
                textAlign: "center",
                transform: `scale(${spring({
                  frame: frame - currentSub.from,
                  fps,
                  config: { damping: 14, stiffness: 180 },
                })})`,
              }}
            >
              <span style={{ fontSize: 27, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
                {currentSub.highlight ? (
                  <>
                    {currentSub.text.split(currentSub.highlight)[0]}
                    <span style={{ color: isOutro ? "#34D399" : goldAccent }}>{currentSub.highlight}</span>
                    {currentSub.text.split(currentSub.highlight)[1]}
                  </>
                ) : (
                  currentSub.text
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};
